import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import FavouriteButton from '@/components/FavouriteButton';
import NewBadge from '@/components/NewBadge';
import useFavourites from '@/context/FavouritesContext';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import axios from 'axios';
import { format } from 'date-fns';
import { SEO } from '@/components/SEO';
import { EventsCountryFilter } from '@/components/EventsCountryFilter';
import { useGeo } from '@/context/GeoContext';
import { formatCurrency, getCountryName, getCountryFlag } from '@/lib/i18n';
import { 
  Search, 
  MapPin, 
  Calendar, 
  Users, 
  Car,
  ChevronRight,
  ChevronDown,
  Check,
  Loader2,
  ArrowLeft,
  Share2,
  LayoutGrid,
  LayoutList,
  Heart,
  Trash2,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SportTagBadge } from '@/components/sports/SportTagBadge';
import { normaliseSport, DEFAULT_SPORT, USER_FACING_SPORTS, getSportMeta } from '@/lib/sportCategories';
import NearMeChip from '@/components/NearMeChip';
import useNearby from '@/context/NearbyContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { filterCountry } = useGeo();
  const nearby = useNearby();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Admin-only inline delete flow. Powered by the same DELETE /events/{id}
  // endpoint that the EventDetails page uses — it already gates on role.
  const isAdmin = !!(user && (user.is_admin || user.role === 'admin'));
  const [deleteTarget, setDeleteTarget] = useState(null); // {event_id, name}
  const [deleting, setDeleting] = useState(false);

  const openDelete = (event, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setDeleteTarget({ event_id: event.event_id, name: event.name });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/events/${deleteTarget.event_id}`);
      setEvents((prev) => prev.filter((e) => e.event_id !== deleteTarget.event_id));
      toast.success('Event deleted');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };
  // Phase 3 — sport filter is now multi-select. Defaults to the user's
  // selected communities so a roller skater only sees roller events on first
  // page load. Empty array = "All" (no filter applied). Pre-Phase-2 users
  // Default = the user's profile sport if we know it (so a signed-up
  // skateboarder / inliner / quad skater sees their own sport first),
  // otherwise "All sports". Whatever the user picks is persisted to
  // localStorage so navigating into an event and back keeps their
  // explicit choice.
  const [selectedSports, setSelectedSports] = useState(() => {
    try {
      const raw = localStorage.getItem('events.selectedSports');
      if (raw !== null) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch { /* fall through */ }
    return Array.isArray(user?.sports) && user.sports.length > 0
      ? user.sports
      : [DEFAULT_SPORT];
  });
  // Persist every change so the choice survives navigation + refresh.
  const setSelectedSportsPersisted = (updater) => {
    setSelectedSports((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try { localStorage.setItem('events.selectedSports', JSON.stringify(next)); } catch { /* ignore quota */ }
      return next;
    });
  };
  const [glowingCards, setGlowingCards] = useState(new Set());
  const observerRef = useRef(null);

  // Card layout — 'full' (current single-column-on-mobile big cards) or
  // 'compact' (2-up tighter cards with smaller thumbnails, matching the
  // "My Events" view). Persisted per-device so a user's preferred density
  // sticks across visits.
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem('events.viewMode') || 'full';
    } catch { return 'full'; }
  });
  const setViewModePersisted = (m) => {
    setViewMode(m);
    try { localStorage.setItem('events.viewMode', m); } catch { /* ignore quota */ }
  };

  // "Show favourites only" toggle — mirrors the same heart filter the
  // user already has on the Calendar. Guests get the same auth-prompt
  // shortcut (`navigate('/register')`) so the heart never silently no-ops.
  const [favouritesOnly, setFavouritesOnly] = useState(false);
  const { isFavourite } = useFavourites();

  // IntersectionObserver for glow effect on mobile
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute('data-event-id');
          if (!id) return;
          if (entry.isIntersecting) {
            setGlowingCards((prev) => new Set([...prev, id]));
            // Remove glow after animation completes
            setTimeout(() => {
              setGlowingCards((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
              });
            }, 1200);
          }
        });
      },
      { threshold: 0.1, rootMargin: '-150px 0px -65% 0px' }
    );
    return () => observerRef.current?.disconnect();
  }, []);

  const cardRef = useCallback((node) => {
    if (node && observerRef.current) {
      observerRef.current.observe(node);
    }
  }, []);

  useEffect(() => {
    fetchEvents(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCountry]);

  // ── Scroll restoration ─────────────────────────────────────────────
  // React Router doesn't restore window scroll by default. When the
  // user taps an event and comes back, we want them to land exactly
  // where they were in the list — not scrolled to the top. Save the
  // scroll position on every scroll (throttled via the last-value
  // pattern) and restore on mount + once events have finished loading
  // (so the list has actually rendered its rows).
  const SCROLL_KEY = 'events.scrollY';
  useEffect(() => {
    let saved = 0;
    const onScroll = () => {
      saved = window.scrollY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    const flush = () => {
      try { sessionStorage.setItem(SCROLL_KEY, String(saved)); } catch { /* quota */ }
    };
    window.addEventListener('pagehide', flush);
    // Also flush right before we unmount so browser back navigations
    // (which don't always fire pagehide) still capture the position.
    return () => {
      flush();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', flush);
    };
  }, []);
  useEffect(() => {
    // Restore only once — after events have finished their first fetch
    // so the DOM has enough height for the scroll to land properly.
    if (loading) return;
    try {
      const raw = sessionStorage.getItem(SCROLL_KEY);
      const y = raw ? parseInt(raw, 10) : 0;
      if (!Number.isNaN(y) && y > 0) {
        // requestAnimationFrame gives the list one paint to lay out
        // before we scroll to y — otherwise we snap to the max
        // scrollable height at that moment, which may be short of y.
        requestAnimationFrame(() => window.scrollTo({ top: y, behavior: 'instant' in window ? 'instant' : 'auto' }));
      }
    } catch { /* ignore */ }
  // Intentionally only depend on the first `loading` → false transition
  // so we don't repeatedly re-scroll on subsequent re-renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const fetchEvents = async (searchTerm = '') => {
    try {
      setLoading(true);
      // limit=200 — production has 50+ live events globally and the
      // backend default of 50 was silently truncating the list when a
      // user picked "from anywhere". 200 keeps the payload bounded but
      // covers the realistic global ceiling for the next 30 days.
      const params = { upcoming_only: true, limit: 200 };
      if (searchTerm) params.search = searchTerm;
      if (filterCountry) params.country = filterCountry;

      const response = await axios.get(`${API}/events`, { params });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEvents(search);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'EEE, MMM d • h:mm a');
    } catch {
      return dateString;
    }
  };

  const renderEventCard = (event) => {
    const isVenue = event.venue_type === 'venue';
    const eventSport = normaliseSport(event.sport || event.category);
    const isSnow = eventSport === 'snow_sports';
    return (
      <Card
        key={event.event_id}
        ref={cardRef}
        data-event-id={event.event_id}
        className={`bg-card border-border/50 hover:border-primary/50 transition-all duration-300 card-hover overflow-hidden cursor-pointer group ${
          glowingCards.has(event.event_id) ? 'event-card-glow' : ''
        } ${isVenue ? 'ring-1 ring-cyan-400/30 hover:ring-cyan-400/60' : ''}`}
        onClick={() => navigate(`/events/${event.event_id}`)}
        data-testid={`event-card-${event.event_id}`}
      >
        <div className="relative h-48 overflow-hidden">
          <NewBadge createdAt={event.created_at} size="md" />
          <img loading="lazy"
            src={event.image_url || "https://images.unsplash.com/photo-1765574782625-198093f8c9ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxzY2VuaWMlMjBoaWdod2F5JTIwcm9hZCUyMHRyaXAlMjBhc3BoYWx0fGVufDB8fHx8MTc3MDU1ODk1MHww&ixlib=rb-4.1.0&q=60&w=800"}
            alt={event.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            style={event.thumbnail_position ? {
              objectPosition: `${event.thumbnail_position.x}% ${event.thumbnail_position.y}%`,
              transform: `scale(${event.thumbnail_position.scale || 1})`,
            } : undefined}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />

          {isVenue && (
            <Badge className={`absolute top-4 left-4 backdrop-blur-sm ${isSnow ? 'bg-cyan-500/90 text-white' : 'bg-pink-500/90 text-white'}`} data-testid="venue-badge">
              {isSnow ? '⛄ Open Daily' : '🛼 Open Daily'}
            </Badge>
          )}
          {!isVenue && event.available_drivers > 0 && (
            <Badge className="absolute top-4 right-4 bg-success text-success-foreground">
              <Car className="w-3 h-3 mr-1" />
              {event.available_drivers} driver{event.available_drivers > 1 ? 's' : ''}
            </Badge>
          )}
          {!isVenue && event.waiting_riders > 0 && (
            <Badge className="absolute top-4 left-4 bg-accent text-accent-foreground">
              <Users className="w-3 h-3 mr-1" />
              {event.waiting_riders} seeking
            </Badge>
          )}

          {/* Favourite heart — floating on the image so it's always
              visible without scrolling. Its own onClick stops the
              card navigation, and the click is gated for guests. */}
          <div className="absolute bottom-3 right-3">
            <FavouriteButton kind="event" id={event.event_id} size="sm" />
          </div>

          {/* Admin-only quick delete — top-right on the image. Perfect
              for cleaning duplicates from the queue without opening
              each event first. */}
          {isAdmin && (
            <button
              type="button"
              onClick={(e) => openDelete(event, e)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-500/90 hover:bg-red-500 backdrop-blur-sm text-white flex items-center justify-center shadow-lg transition-all hover:scale-110"
              title="Admin: delete this event"
              data-testid={`admin-delete-event-${event.event_id}`}
              aria-label="Delete event"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        <CardContent className="p-5 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-xl line-clamp-1 flex-1">{event.name}</h3>
            <SportTagBadge sport={eventSport} size="xs" variant="ghost" />
          </div>

          {isVenue ? (
            event.opening_hours && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="w-4 h-4 text-cyan-400" />
                <span className="text-xs line-clamp-2">{event.opening_hours}</span>
              </div>
            )
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4 text-primary" />
              <span className="text-sm">{formatDate(event.date)}</span>
            </div>
          )}

          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="w-4 h-4 text-secondary" />
            <span className="text-sm line-clamp-1">{event.location?.address}</span>
          </div>

          {event.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">{event.description}</p>
          )}

          <div className="pt-2 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {!isVenue && event.organiser_name && event.organiser_name.toLowerCase() !== 'admin' && event.organiser_name !== 'RollM8s Team'
                ? `By ${event.organiser_name}` : ''}
            </span>
            <ChevronRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
          </div>
        </CardContent>
      </Card>
    );
  };

  // Compact 2-up tile — matches the "My Events" / Bookings view the user
  // showed in the screen recording. Smaller image, tighter padding, just
  // the essentials (title + date + venue/driver pill). Keeps the same
  // navigation + glow + favourite-heart behaviours as the full card.
  const renderCompactEventCard = (event) => {
    const isVenue = event.venue_type === 'venue';
    const eventSport = normaliseSport(event.sport || event.category);
    return (
      <Card
        key={event.event_id}
        ref={cardRef}
        data-event-id={event.event_id}
        className={`bg-card border-border/50 hover:border-primary/50 transition-all duration-300 overflow-hidden cursor-pointer group ${
          glowingCards.has(event.event_id) ? 'event-card-glow' : ''
        } ${isVenue ? 'ring-1 ring-cyan-400/30 hover:ring-cyan-400/60' : ''}`}
        onClick={() => navigate(`/events/${event.event_id}`)}
        data-testid={`event-card-compact-${event.event_id}`}
      >
        <div className="relative aspect-[4/3] overflow-hidden">
          <NewBadge createdAt={event.created_at} size="sm" />
          <img loading="lazy"
            src={event.image_url || "https://images.unsplash.com/photo-1765574782625-198093f8c9ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxzY2VuaWMlMjBoaWdod2F5JTIwcm9hZCUyMHRyaXAlMjBhc3BoYWx0fGVufDB8fHx8MTc3MDU1ODk1MHww&ixlib=rb-4.1.0&q=60&w=600"}
            alt={event.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            style={event.thumbnail_position ? {
              objectPosition: `${event.thumbnail_position.x}% ${event.thumbnail_position.y}%`,
              transform: `scale(${event.thumbnail_position.scale || 1})`,
            } : undefined}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/85 via-background/0 to-transparent" />
          {isVenue && (
            <Badge
              className="absolute top-1.5 left-1.5 text-[10px] px-1.5 py-0 h-5 backdrop-blur-sm bg-pink-500/90 text-white"
              data-testid="venue-badge-compact"
            >
              Open Daily
            </Badge>
          )}
          {!isVenue && event.available_drivers > 0 && (
            <Badge className="absolute top-1.5 right-1.5 text-[10px] px-1.5 py-0 h-5 bg-success text-success-foreground">
              <Car className="w-2.5 h-2.5 mr-0.5" />
              {event.available_drivers}
            </Badge>
          )}
          <div className="absolute bottom-1.5 right-1.5">
            <FavouriteButton kind="event" id={event.event_id} size="xs" />
          </div>
          {isAdmin && (
            <button
              type="button"
              onClick={(e) => openDelete(event, e)}
              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-red-500/90 hover:bg-red-500 backdrop-blur-sm text-white flex items-center justify-center shadow-md transition-all hover:scale-110"
              title="Admin: delete this event"
              data-testid={`admin-delete-event-compact-${event.event_id}`}
              aria-label="Delete event"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
        <CardContent className="p-2.5 space-y-1">
          <h3 className="font-semibold text-sm leading-tight line-clamp-2 min-h-[2.4em]">
            {event.name}
          </h3>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Calendar className="w-3 h-3 text-primary flex-shrink-0" />
            <span className="text-[11px] line-clamp-1">
              {isVenue ? (event.opening_hours || 'Open daily') : formatDate(event.date)}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <MapPin className="w-3 h-3 text-secondary flex-shrink-0" />
            <span className="text-[11px] line-clamp-1">{event.location?.address || '—'}</span>
          </div>
          <div className="pt-0.5 flex items-center justify-between">
            <SportTagBadge sport={eventSport} size="xs" variant="ghost" />
            <ChevronRight className="w-3.5 h-3.5 text-primary opacity-60 group-hover:translate-x-0.5 group-hover:opacity-100 transition" />
          </div>
        </CardContent>
      </Card>
    );
  };

  // Single dispatcher so the page sections don't need to branch on the mode.
  const renderCard = (event) =>
    viewMode === 'compact' ? renderCompactEventCard(event) : renderEventCard(event);

  // Grid classes change with mode: compact forces 2-up on mobile (matching
  // the screen-recording layout), full keeps single-column on mobile so
  // the big-impact cards stay legible.
  const gridClass = viewMode === 'compact'
    ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3'
    : 'grid md:grid-cols-2 lg:grid-cols-3 gap-6';


  return (
    <div className="min-h-screen">
      <SEO
        title="Skate, Wheels & Boards Events"
        description="Browse and discover skate, wheel and board events across the UK. Roller skating, skateboard, inline, BMX, scooter, longboard and snow sports — find events, get tickets and share rides with fellow community members."
        path="/events"
        keywords="skate events UK, skateboard events, BMX events, inline skating events UK, scooter events, roller disco near me, skating nights, quad skating events"
      />
      {/* Back to Home - via Navbar logo */}

      {/* Navigation */}
      <Navbar activePage="/events" />

      {/* Main Content */}
      <main className="pb-12 px-4 md:px-8 pt-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 relative">
            <h1 className="font-heading text-4xl md:text-5xl uppercase mb-4">
              Upcoming <span className="gradient-text">Events</span>
            </h1>
            <div className="absolute right-0 top-0 flex items-center gap-2">
              <button
                onClick={async () => {
                  const shareUrl = `${window.location.origin}/api/share/events`;
                  const text = "UK skate events — discover, RSVP & ride together 🛼🎫";
                  if (navigator.share) {
                    try {
                      await navigator.share({ title: 'RollM8s — Events', text, url: shareUrl });
                      return;
                    } catch (e) {
                      if (e?.name === 'AbortError') return;
                    }
                  }
                  try {
                    await navigator.clipboard.writeText(shareUrl);
                    toast.success('Events link copied — paste it in your chat!');
                  } catch {
                    toast.error('Could not copy — long-press to copy manually');
                  }
                }}
                className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/40 hover:bg-card/70 hover:border-border transition px-3 h-10 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="share-events-btn"
                aria-label="Share Events page"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </button>
            </div>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Want to list your own event? Head to your <a href="/dashboard" className="text-primary hover:underline">Dashboard</a> to create one
            </p>
            <p className="text-sm text-muted-foreground/60 mt-2">
              Find skate events across the UK and connect with drivers heading your way
            </p>
          </div>

          {/* Filters first — country + near-me sit side-by-side on a
              single row to keep the chip count low. */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <EventsCountryFilter testId="events-country-filter" />
            <NearMeChip autoPrompt />
          </div>

          {/* Sport filter — white dropdown, multi-select. Defaults to user's
              communities so each tribe sees their own world. */}
          <div className="mb-4" data-testid="events-sport-filter">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-full md:w-auto md:min-w-[260px] inline-flex items-center justify-between gap-2 rounded-full bg-white text-zinc-900 border border-zinc-300 px-4 h-11 text-sm font-medium shadow-sm hover:bg-zinc-50 active:scale-[0.99] transition"
                  data-testid="events-sport-dropdown-trigger"
                >
                  <span className="truncate">
                    {selectedSports.length === 0
                      ? '✨  All sports'
                      : selectedSports.length === 1
                        ? `${getSportMeta(selectedSports[0]).emoji || ''}  ${getSportMeta(selectedSports[0]).short}`
                        : `${selectedSports.length} sports selected`}
                  </span>
                  <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[260px] p-1 bg-white text-zinc-900 border border-zinc-200 shadow-xl"
                align="start"
                data-testid="events-sport-dropdown-panel"
              >
                <button
                  type="button"
                  onClick={() => setSelectedSportsPersisted([])}
                  className="w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-zinc-100 active:bg-zinc-200"
                  data-testid="events-sport-option-all"
                >
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    {selectedSports.length === 0 && <Check className="w-4 h-4 text-pink-500" />}
                  </span>
                  <span className="font-medium">✨ All sports</span>
                </button>
                <div className="my-1 h-px bg-zinc-200" />
                <div className="max-h-72 overflow-y-auto">
                  {USER_FACING_SPORTS.map((sportId) => {
                    const meta = getSportMeta(sportId);
                    const checked = selectedSports.includes(sportId);
                    return (
                      <button
                        key={sportId}
                        type="button"
                        onClick={() => {
                          setSelectedSportsPersisted((prev) =>
                            prev.includes(sportId)
                              ? prev.filter((s) => s !== sportId)
                              : [...prev, sportId],
                          );
                        }}
                        className="w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-zinc-100 active:bg-zinc-200"
                        data-testid={`events-sport-option-${sportId}`}
                      >
                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          {checked && <Check className="w-4 h-4 text-pink-500" />}
                        </span>
                        {meta.emoji && <span aria-hidden>{meta.emoji}</span>}
                        <span className="font-medium">{meta.short}</span>
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="max-w-xl mx-auto mb-10">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search events or locations..."
                className="pl-12 pr-24 h-14 rounded-full bg-card border-border/50 focus:border-primary text-lg"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="event-search-input"
              />
              <Button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-primary hover:bg-primary/90"
                data-testid="event-search-btn"
              >
                Search
              </Button>
            </div>
          </form>

          {/* Controls — Favourites filter + Layout toggle. Same heart UX
              as the Calendar page so users see one consistent affordance
              across the app. */}
          <div className="flex items-center justify-end gap-2 mb-3" data-testid="events-view-toggle-row">
            <button
              type="button"
              onClick={() => user ? setFavouritesOnly(v => !v) : navigate('/register')}
              className={`h-9 w-9 inline-flex items-center justify-center rounded-full border transition ${
                favouritesOnly
                  ? 'bg-rose-500/15 border-rose-500/60 text-rose-400'
                  : 'bg-card/40 border-border/60 text-muted-foreground hover:text-rose-400 hover:border-rose-400/50'
              }`}
              aria-pressed={favouritesOnly}
              aria-label={favouritesOnly ? 'Show all events' : 'Show only favourites'}
              title={favouritesOnly ? 'Showing favourites only' : 'Show favourites only'}
              data-testid="events-favourites-toggle"
            >
              <Heart className="w-4 h-4" fill={favouritesOnly ? 'currentColor' : 'none'} />
            </button>
            <div
              className="inline-flex items-center rounded-full border border-border/60 bg-card/40 overflow-hidden"
              role="group"
              aria-label="Layout"
            >
              <button
                type="button"
                onClick={() => setViewModePersisted('full')}
                className={`h-9 w-9 inline-flex items-center justify-center transition ${
                  viewMode === 'full'
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                aria-pressed={viewMode === 'full'}
                aria-label="Single column"
                title="Single column"
                data-testid="events-view-full"
              >
                <LayoutList className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewModePersisted('compact')}
                className={`h-9 w-9 inline-flex items-center justify-center transition ${
                  viewMode === 'compact'
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                aria-pressed={viewMode === 'compact'}
                aria-label="Compact grid"
                title="Compact grid"
                data-testid="events-view-compact"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Events Grid — split into Permanent Venues + Upcoming Events */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
            </div>
          ) : (() => {
            // Empty selection = "All" pill active = show every sport
            const sportFiltered = selectedSports.length === 0
              ? events
              : events.filter(e => selectedSports.includes(normaliseSport(e.sport || e.category)));
            // Near-me radius filter on top of sport filter.
            //
            // Bug-fix: when the user has explicitly chosen "From
            // anywhere" (no country filter) the global intent must
            // override Near-me — otherwise picking the global option
            // silently leaves Near-me biting and the user sees only
            // events within their radius. We also separately track the
            // hidden count so we can show an inline "X hidden by
            // Near-me" notice instead of disappearing them silently.
            const nearbyActive = nearby.enabled && !!filterCountry;
            const nearbyHiddenCount = nearbyActive
              ? sportFiltered.filter((e) => !nearby.isWithin(e?.location)).length
              : 0;
            const filtered = nearbyActive
              ? sportFiltered.filter((e) => nearby.isWithin(e?.location))
              : sportFiltered;
            // "Favourites only" — last in the pipeline so it applies on top
            // of country/sport/near-me filters.
            const filteredWithFavs = favouritesOnly
              ? filtered.filter(e => isFavourite('event', e.event_id))
              : filtered;
            const venues = filteredWithFavs.filter(e => e.venue_type === 'venue');
            const realEvents = filteredWithFavs.filter(e => e.venue_type !== 'venue');

            if (filteredWithFavs.length === 0) {
              // Friendly explanation when the country filter narrows to nothing.
              const filteredCountryFlag = filterCountry ? getCountryFlag(filterCountry) : '';
              const filteredCountryName = filterCountry ? getCountryName(filterCountry) : '';
              const sportEmpty = selectedSports.length > 0 && selectedSports.length < 100;
              // If Near-me has eaten every single event, surface that first
              // so the empty state isn't silently misleading.
              if (nearbyHiddenCount > 0) {
                return (
                  <div className="text-center py-20" data-testid="events-empty">
                    <div
                      className="mx-auto max-w-md mb-6 rounded-2xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 flex items-center gap-3 text-sm"
                      data-testid="nearby-hidden-banner"
                    >
                      <span className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)] flex-shrink-0" />
                      <p className="flex-1 text-cyan-100 text-left">
                        <span className="font-bold">{nearbyHiddenCount} event{nearbyHiddenCount === 1 ? '' : 's'}</span>{' '}
                        hidden by Near-me ({nearby.radiusMi} mi).
                      </p>
                      <button
                        type="button"
                        onClick={() => nearby.disable?.()}
                        className="text-xs font-bold uppercase tracking-wider text-cyan-300 hover:text-cyan-100"
                        data-testid="nearby-hidden-disable-btn"
                      >
                        Show all
                      </button>
                    </div>
                    <p className="text-xl text-muted-foreground">
                      Nothing inside your Near-me radius right now.
                    </p>
                    <p className="text-sm text-muted-foreground/80 mt-1">
                      Widen the radius, switch off Near-me, or browse globally.
                    </p>
                  </div>
                );
              }
              return (
                <div className="text-center py-20" data-testid="events-empty">
                  {favouritesOnly ? (
                    <>
                      <Heart className="w-10 h-10 mx-auto mb-3 text-rose-400/70" />
                      <p className="text-xl text-muted-foreground mb-2">
                        No favourite events yet
                      </p>
                      <p className="text-sm text-muted-foreground/80 mb-4">
                        Tap the heart on any event card to save it here.
                      </p>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => setFavouritesOnly(false)}
                        className="rounded-full"
                        data-testid="events-clear-favs-filter"
                      >
                        Show all events
                      </Button>
                    </>
                  ) : filterCountry && events.length === 0 ? (
                    <>
                      <p className="text-2xl mb-2" aria-hidden="true">{filteredCountryFlag}</p>
                      <p className="text-xl text-muted-foreground mb-2">
                        No events in {filteredCountryName} yet
                      </p>
                      <p className="text-sm text-muted-foreground mb-6">
                        Be the first to host one — or switch the filter to <span className="text-foreground">All countries</span> to explore.
                      </p>
                    </>
                  ) : (
                    <p className="text-xl text-muted-foreground mb-4">
                      {sportEmpty
                        ? `No events for your selected sport${selectedSports.length > 1 ? 's' : ''} yet — tap All to browse everything`
                        : 'No upcoming events found'}
                    </p>
                  )}
                  {user?.role === 'organiser' && (
                    <Button onClick={() => navigate('/create-event')} className="rounded-full bg-primary hover:bg-primary/90">
                      Upload Flyer / Create Event
                    </Button>
                  )}
                </div>
              );
            }

            return (
              <>
                {nearbyHiddenCount > 0 && (
                  <div
                    className="mb-6 rounded-2xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 flex items-center gap-3 text-sm"
                    data-testid="nearby-hidden-banner"
                  >
                    <span className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)] flex-shrink-0" />
                    <p className="flex-1 text-cyan-100">
                      <span className="font-bold">{nearbyHiddenCount} event{nearbyHiddenCount === 1 ? '' : 's'}</span>{' '}
                      hidden by Near-me ({nearby.radiusMi} mi).
                    </p>
                    <button
                      type="button"
                      onClick={() => nearby.disable?.()}
                      className="text-xs font-bold uppercase tracking-wider text-cyan-300 hover:text-cyan-100"
                      data-testid="nearby-hidden-disable-btn"
                    >
                      Show all
                    </button>
                  </div>
                )}
                {venues.length > 0 && (
                  <section className="mb-14" data-testid="permanent-venues-section">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="h-px flex-1 bg-gradient-to-r from-transparent to-primary/40" />
                      <h2 className="text-sm uppercase tracking-[0.2em] font-bold text-primary">
                        🏔️ Permanent Venues
                      </h2>
                      <div className="h-px flex-1 bg-gradient-to-l from-transparent to-primary/40" />
                    </div>
                    <div className={gridClass}>
                      {venues.map(renderCard)}
                    </div>
                  </section>
                )}
                {realEvents.length > 0 && (
                  <section data-testid="upcoming-events-section">
                    {venues.length > 0 && (
                      <div className="flex items-center gap-3 mb-5">
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent to-muted-foreground/30" />
                        <h2 className="text-sm uppercase tracking-[0.2em] font-bold text-muted-foreground">
                          📅 Upcoming Events
                        </h2>
                        <div className="h-px flex-1 bg-gradient-to-l from-transparent to-muted-foreground/30" />
                      </div>
                    )}
                    <div className={gridClass}>
                      {realEvents.map(renderCard)}
                    </div>
                  </section>
                )}
              </>
            );
          })()}
        </div>
      </main>

      {/* Admin-only confirm-before-delete dialog. Reused by both card
          renderers via the shared `deleteTarget` state. */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="admin-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this event?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.name
                ? <>You&apos;re about to permanently delete <span className="font-semibold text-foreground">{deleteTarget.name}</span>. This can&apos;t be undone. Any associated ride-share trips will also be removed.</>
                : 'You\'re about to permanently delete this event. This can\'t be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="admin-delete-confirm"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete event'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EventsPage;
